import { useState, type ReactElement } from 'react';
import { ChevronDown, Copy, KeyRound } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  useCreateScimCredential,
  useDisconnectScimProvisioning,
  useScimProvisioning,
} from '@/hooks/api/useScimProvisioningApi';
import { useToast } from '@/hooks/use-toast';
import { formatZonedDate } from '@/lib/date-utils';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';

interface ScimProvisioningModalProps {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Owner/manager may generate/turn off; support opens it read-only. */
  canManage: boolean;
}

/**
 * Management modal for SCIM provisioning (KIR-4693) — a single per-org
 * connection, like SIEM. Shows the SCIM URL, the current connection (if any),
 * lets an owner/manager generate/rotate the token (revealed once) and turn
 * it off. The one-time bearer lives in local state only.
 */
export function ScimProvisioningModal({
  organizationId,
  open,
  onOpenChange,
  canManage,
}: ScimProvisioningModalProps): ReactElement {
  const timeZone = useDisplayTimeZone();
  const [name, setName] = useState('');
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { toast } = useToast();

  const { data, isLoading, isError } = useScimProvisioning(
    organizationId,
    open
  );
  const createMutation = useCreateScimCredential(organizationId);
  const disconnectMutation = useDisconnectScimProvisioning(organizationId);

  const scimUrl = data?.scimUrl ?? '';
  const credential = data?.credential ?? null;
  const connected = credential !== null;

  const copy = async (value: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied` });
    } catch {
      toast({
        title: `Could not copy the ${label.toLowerCase()}`,
        description: 'Copy it manually instead.',
        variant: 'destructive',
      });
    }
  };

  const handleGenerate = async (): Promise<void> => {
    try {
      const created = await createMutation.mutateAsync({ name: name.trim() });
      setName('');
      setRevealedToken(created.bearerToken);
      createMutation.reset();
    } catch {
      toast({
        title: 'Could not generate the SCIM token',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDisconnect = (): void => {
    disconnectMutation.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: 'SCIM provisioning turned off',
          description: 'Your IdP can no longer provision or revoke users.',
        });
        setConfirmDisconnect(false);
        setRevealedToken(null);
      },
      onError: () => {
        toast({
          title: 'Could not turn off SCIM provisioning',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  const handleOpenChange = (next: boolean): void => {
    if (!next) {
      setName('');
      setRevealedToken(null);
    }
    onOpenChange(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>SCIM Provisioning</DialogTitle>
            <DialogDescription>
              Point your identity provider at the SCIM URL and token below.
              Offboarding a user there revokes their Kirin access automatically.
              The token is shown once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* ── Endpoint ── the SCIM URL the IdP points at */}
            <section className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Endpoint
              </Label>
              {scimUrl ? (
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-3 py-2 text-sm">
                    {scimUrl}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void copy(scimUrl, 'SCIM URL')}
                    aria-label="Copy SCIM URL"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  SCIM is not configured for this environment yet. Contact
                  support to enable it.
                </p>
              )}
            </section>

            {/* ── Status ── the current token (hidden) + generate/regenerate */}
            <section className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Status
              </Label>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : isError ? (
                <p className="text-sm text-destructive">
                  Could not load the SCIM connection. Please refresh.
                </p>
              ) : connected ? (
                <div className="flex items-start gap-3 rounded-md border px-3 py-2.5">
                  <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 space-y-0.5">
                    <p className="truncate text-sm font-medium">
                      {credential.name ?? 'SCIM token'}
                    </p>
                    {/* Token is never re-readable — only the one-time reveal on
                        create shows it. Suppress "hidden" while that reveal is up
                        so the two lines don't contradict each other (KIR-4441). */}
                    {!revealedToken && (
                      <p className="text-xs text-muted-foreground">
                        Token created · hidden for security
                      </p>
                    )}
                    <p className="truncate text-xs text-muted-foreground">
                      <code>{credential.keyPrefix}…</code> · configured{' '}
                      {formatZonedDate(credential.createdAt, timeZone)}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  SCIM is not connected yet.
                </p>
              )}

              {revealedToken && (
                <div className="space-y-1.5 rounded-md border border-status-warn-border bg-status-warn-background/40 p-3">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    New SCIM token — copy it now, shown once
                  </Label>
                  <div className="flex items-start gap-2">
                    <code className="min-w-0 flex-1 break-all rounded-md bg-muted px-3 py-2 font-mono text-sm">
                      {revealedToken}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void copy(revealedToken, 'SCIM token')}
                      aria-label="Copy SCIM token"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}

              {canManage && scimUrl && (
                <div className="flex items-end gap-2 pt-1">
                  <div className="flex-1 space-y-1.5">
                    <Label htmlFor="scim-token-name">
                      {connected ? 'Regenerate token' : 'Generate a token'}
                    </Label>
                    <Input
                      id="scim-token-name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Name this token"
                      maxLength={100}
                    />
                  </div>
                  <Button
                    onClick={() => void handleGenerate()}
                    disabled={
                      name.trim().length === 0 || createMutation.isPending
                    }
                  >
                    {createMutation.isPending
                      ? 'Generating…'
                      : connected
                        ? 'Regenerate'
                        : 'Generate'}
                  </Button>
                </div>
              )}
              {connected && canManage && (
                <p className="text-xs text-muted-foreground">
                  Regenerating replaces the current token — update your IdP with
                  the new one.
                </p>
              )}
            </section>

            {/* ── Turn off ── one off switch, replacing the old inline
                "Disconnect" (Kirin doesn't dial out; the IdP pushes to us, so
                there is nothing to "disconnect" — KIR-4441). */}
            {connected && canManage && (
              <section className="space-y-1.5 border-t pt-4">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Turn off
                </Label>
                <p className="text-xs text-muted-foreground">
                  Deactivates the token so your IdP can no longer provision or
                  revoke users. Generate a new one to turn it back on.
                </p>
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmDisconnect(true)}
                >
                  Turn off SCIM provisioning
                </Button>
              </section>
            )}

            <Collapsible open={helpOpen} onOpenChange={setHelpOpen}>
              <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-muted/50">
                How it works
                <ChevronDown
                  className={`h-4 w-4 shrink-0 transition-transform ${helpOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-3 rounded-md border border-t-0 p-3">
                <p className="text-sm text-muted-foreground">
                  Offboarding a user in your identity provider revokes their
                  Kirin access — API keys revoked and sessions ended, within
                  minutes. Creating or updating a user never grants or changes
                  Kirin access on its own — but your IdP must still create
                  (link) the user first, or there&apos;s no linked account for
                  it to deactivate later.
                </p>

                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Setup
                  </Label>
                  <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    <li>
                      Point your IdP&apos;s SCIM at the endpoint above, with the
                      token as the bearer.
                    </li>
                    <li>
                      <span className="font-medium text-foreground">
                        Enable Create Users and Deactivate Users
                      </span>{' '}
                      in that SCIM app (in Okta: Provisioning → To App) — both
                      are off by default. Create links the user so a later
                      deactivate has an account to act on; Deactivate is what
                      reaches Kirin to revoke access. Enable Update User
                      Attributes too, so the email we match on stays in sync.
                      Then assign the users or groups you want covered.
                    </li>
                    <li>
                      <span className="font-medium text-foreground">
                        Set the SCIM unique identifier to the user&apos;s email
                      </span>{' '}
                      — not an internal id or username, or offboarding can miss
                      the right account.
                    </li>
                    <li>
                      <span className="font-medium text-foreground">
                        Verify once
                      </span>{' '}
                      — provision a test user, sign in via SSO as them, then
                      offboard them in your IdP and confirm their Kirin access
                      is revoked. A silent identifier mismatch otherwise goes
                      unnoticed until a real offboarding fails.
                    </li>
                  </ul>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        isOpen={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Turn off SCIM provisioning?"
        description="This deactivates the token immediately — your IdP will stop provisioning and revoking users. Generate a new one to turn it back on."
        confirmLabel="Turn off"
        confirmingLabel="Turning off…"
        isLoading={disconnectMutation.isPending}
        variant="destructive"
      />
    </>
  );
}

ScimProvisioningModal.displayName = 'ScimProvisioningModal';
